import React, { useState, useEffect } from "react";
import api from "../../../../utils/api";
import { sliderApi, noticeApi } from "../../../../api/api";
import { ChevronLeft, ChevronRight, BellRing } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_BANNER = "/assets/banner-1.jpg";

const HeroSection = () => {
    const [sliderImages, setSliderImages] = useState([]);
    const [notice, setNotice] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [validImages, setValidImages] = useState([]);
    const delay = 6000;

    // Fetch notice API
    useEffect(() => {
        api.get(`${noticeApi}`)
            .then((res) => {
                const apiData = res?.data?.data || [];
                setNotice(apiData);
            })
            .catch((err) => console.error("Notice API Error:", err));
    }, []);

    // Fetch slider images
    useEffect(() => {
        setLoading(true);
        api.get(`${sliderApi}`)
            .then((res) => {
                const apiData = res?.data?.data || [];
                if (Array.isArray(apiData) && apiData.length > 0) {
                    const formatted = apiData.map((item) => ({
                        image: item.image || null,
                        link: item.link || null,
                        title: item.title || "",
                        subtitle: item.subtitle || "",
                    }));
                    setSliderImages(formatted);
                } else {
                    setValidImages([
                        {
                            image: DEFAULT_BANNER,
                            title: "Welcome to PYQ Pedia",
                            subtitle: "Your journey to success starts here.",
                        },
                    ]);
                }
            })
            .catch((err) => {
                console.error("Slider API Error:", err);
                setValidImages([
                    {
                        image: DEFAULT_BANNER,
                        title: "Excellence in Learning",
                        subtitle: "Access premium resources today.",
                    },
                ]);
            })
            .finally(() => setLoading(false));
    }, []);

    // Image Validation
    useEffect(() => {
        if (sliderImages.length === 0) return;
        const validateImages = async () => {
            const checks = await Promise.all(
                sliderImages.map(
                    (item) =>
                        new Promise((resolve) => {
                            const img = new Image();
                            const testSrc = item.image || DEFAULT_BANNER;
                            img.src = testSrc;
                            img.onload = () =>
                                resolve({ ...item, image: testSrc });
                            img.onerror = () =>
                                resolve({ ...item, image: DEFAULT_BANNER });
                        }),
                ),
            );
            setValidImages(checks);
        };
        validateImages();
    }, [sliderImages]);

    // Auto slide
    useEffect(() => {
        if (validImages.length <= 1) return;
        const timer = setInterval(() => nextSlide(), delay);
        return () => clearInterval(timer);
    }, [currentIndex, validImages]);

    const nextSlide = () =>
        setCurrentIndex((prev) =>
            prev === validImages.length - 1 ? 0 : prev + 1,
        );
    const prevSlide = () =>
        setCurrentIndex((prev) =>
            prev === 0 ? validImages.length - 1 : prev - 1,
        );

    if (loading) {
        return (
            <section className="w-full flex flex-col bg-gray-100 animate-pulse">
                <div className="w-full aspect-[4/5] md:aspect-[21/9] bg-gray-200"></div>
                <div className="h-12 w-full bg-neutral-900 flex items-center px-4 md:px-8">
                    <div className="h-4 bg-neutral-700 w-32 md:w-48 rounded"></div>
                </div>
            </section>
        );
    }

    return (
        <>
            <style>{`
                @keyframes ticker-slide {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                .animate-ticker {
                  display: inline-flex;
                  animation: ticker-slide 30s linear infinite;
                }
                .animate-ticker:hover {
                  animation-play-state: paused;
                }
            `}</style>

            <section className="w-full relative overflow-hidden flex flex-col group">
                {/* Responsive Container: Taller on mobile (4/5), wider on desktop (21/9) */}
                <div className="relative w-full aspect-[4/5] sm:aspect-[16/9] md:aspect-[25/9] bg-neutral-200 overflow-hidden">
                    <AnimatePresence mode="wait">
                        {validImages.map(
                            (item, index) =>
                                index === currentIndex && (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{
                                            duration: 0.8,
                                            ease: "easeInOut",
                                        }}
                                        className="absolute inset-0 w-full h-full"
                                    >
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-full object-cover"
                                        />

                                        {/* Responsive Gradient: Darker on bottom for text readability */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>

                                        {/* Content Wrapper */}
                                        <div className="absolute inset-0 flex flex-col justify-end p-6 sm:p-10 md:p-16 lg:p-20">
                                            <motion.div
                                                initial={{ y: 20, opacity: 0 }}
                                                animate={{ y: 0, opacity: 1 }}
                                                transition={{
                                                    delay: 0.2,
                                                    duration: 0.5,
                                                }}
                                                className="max-w-4xl"
                                            >
                                                {item.title && (
                                                    <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2 md:mb-4 leading-tight">
                                                        {item.title}
                                                    </h1>
                                                )}
                                                {item.subtitle && (
                                                    <p className="text-xs sm:text-base md:text-lg lg:text-xl text-gray-200 font-medium max-w-2xl leading-snug mb-6 md:mb-8 line-clamp-3 md:line-clamp-none">
                                                        {item.subtitle}
                                                    </p>
                                                )}

                                                {item.link && (
                                                    <button
                                                        onClick={() =>
                                                            window.open(
                                                                item.link,
                                                                "_blank",
                                                            )
                                                        }
                                                        className="px-6 py-2.5 md:px-8 md:py-3 bg-red-700 hover:bg-red-600 text-white font-bold rounded shadow-lg transition-all active:scale-95 uppercase tracking-widest text-[10px] md:text-xs"
                                                    >
                                                        Explore Now
                                                    </button>
                                                )}
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                ),
                        )}
                    </AnimatePresence>

                    {/* Navigation - Hidden on very small screens to save space */}
                    {validImages.length > 1 && (
                        <>
                            <button
                                onClick={prevSlide}
                                className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all opacity-0 group-hover:opacity-100"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={nextSlide}
                                className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-30 w-12 h-12 items-center justify-center rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all opacity-0 group-hover:opacity-100"
                            >
                                <ChevronRight size={24} />
                            </button>

                            {/* Indicators - Smaller for mobile */}
                            <div className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 md:gap-3">
                                {validImages.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setCurrentIndex(idx)}
                                        className={`h-1 md:h-1.5 rounded-full transition-all duration-500 ${currentIndex === idx ? "w-6 md:w-8 bg-yellow-500 shadow-[0_0_8px_#eab308]" : "w-1.5 md:w-2 bg-white/40"}`}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* News Ticker - Adjusted for mobile */}
                {notice && notice.length > 0 && (
                    <div className="w-full bg-neutral-900 border-t border-white/10 flex items-center h-10 md:h-12">
                        <div className="h-full bg-red-700 px-3 md:px-6 flex items-center gap-2 relative z-20 shrink-0">
                            <BellRing
                                size={14}
                                className="text-white animate-bounce"
                            />
                            <span className="text-white text-[8px] md:text-[10px] font-black uppercase tracking-wider whitespace-nowrap">
                                Updates
                            </span>
                        </div>

                        <div className="flex-1 overflow-hidden relative h-full flex items-center">
                            <div className="animate-ticker whitespace-nowrap flex items-center">
                                {[...notice, ...notice].map((item, idx) => (
                                    <div
                                        key={`${item.id}-${idx}`}
                                        onClick={() =>
                                            item.link &&
                                            window.open(item.link, "_blank")
                                        }
                                        className="inline-flex items-center px-6 md:px-10 text-gray-300 text-[11px] md:text-sm font-bold hover:text-yellow-400 cursor-pointer"
                                    >
                                        <span className="w-1 h-1 rounded-full bg-red-600 mr-2 md:mr-3"></span>
                                        {item.title}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>
        </>
    );
};

export default HeroSection;
